import { Component, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { MatCardModule } from '@angular/material/card';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatTooltipModule } from '@angular/material/tooltip';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import * as Highcharts from 'highcharts';
import { HighchartsChartModule } from 'highcharts-angular';
import { AppUrlConstants } from '../../core/constants/AppUrlConstants';

const KPI_COLORS = ['#3f51b5', '#4caf50', '#ff9800', '#f44336', '#9c27b0', '#00bcd4', '#795548', '#607d8b'];
const INTERNAL_API_KEY = '9fK7#Lm2@Qx8!Vz4$Wp1^Rs6&Ty0*Ub3%Je5?Nd7+Hg9=Ka2';

const DATE_PATTERN = /^\d{4}$|^\d{4}[-/]\d{2}|\b(jan|feb|mar|apr|may|jun|jul|aug|sep|oct|nov|dec)\b|^Q[1-4][-\s]?\d{0,4}|^(wk|week)\s*\d+/i;

export interface AutoWidget {
  type: 'kpi-group' | 'chart' | 'table' | 'message';
  subtype?: 'historical' | 'forecast' | 'breakdown';
  title: string;
  col: number;
  kpis?: { label: string; value: any; color: string }[];
  chartOptions?: Highcharts.Options;
  columns?: string[];
  rows?: string[][];
  message?: string;
  id?: string;
}

@Component({
  selector: 'app-dynamic-dashboard-v2',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    HighchartsChartModule,
    MatCardModule,
    MatButtonModule,
    MatIconModule,
    MatTooltipModule,
    MatFormFieldModule,
    MatInputModule,
    MatProgressSpinnerModule
  ],
  templateUrl: './dynamic-dashboard-v2.component.html',
  styleUrl: './dynamic-dashboard-v2.component.css'
})
export class DynamicDashboardV2Component implements OnInit {
  Highcharts: typeof Highcharts = Highcharts;

  widgets: AutoWidget[] = [];
  chartRefs = new Map<string, Highcharts.Chart>();

  // NL Ask
  userQuestion: string = '';
  analyzing: boolean = false;
  nlError: string = '';

  private widgetSeq = 0;

  // ANALYTICS_WITH_FORECAST response
  forecastResponse: any = null;
  activeView: 'historical' | 'forecast' | 'both' = 'both';

  constructor(private http: HttpClient) {}

  ngOnInit(): void {
    // Empty state on load — user must submit a question first
  }

  analyzeQuestion(): void {
    if (!this.userQuestion.trim()) return;
    this.analyzing = true;
    this.nlError = '';
    this.forecastResponse = null;

    const headers = new HttpHeaders({ 'X-API-Key': INTERNAL_API_KEY, 'X-Skip-Spinner': 'true' });
    this.http.post<any>(AppUrlConstants.ANALYTICS_DASHBOARD_ASK_URL,
      { userQuestion: this.userQuestion }, { headers }
    ).subscribe({
      next: (res) => {
        this.analyzing = false;
        if (res.error) { this.nlError = res.error; return; }

        if (res.descriptive || res.forecast) {
          this.forecastResponse = res;
          this.activeView = 'both';
          this.renderAnalyticsWithForecast(res);
        } else {
          this.renderFromResponse(res);
        }
      },
      error: (err) => {
        this.analyzing = false;
        this.nlError = err?.error?.error || err?.message || 'Request failed';
      }
    });
  }

  toggleView(view: 'historical' | 'forecast' | 'both'): void {
    this.activeView = view;
    if (this.forecastResponse) this.renderAnalyticsWithForecast(this.forecastResponse);
  }

  isChartVisible(widget: AutoWidget): boolean {
    if (!widget.subtype || widget.subtype === 'breakdown') return true;
    if (widget.subtype === 'historical') return this.activeView !== 'forecast';
    if (widget.subtype === 'forecast') return this.activeView !== 'historical';
    return true;
  }

  private renderAnalyticsWithForecast(res: any): void {
    const desc      = res.descriptive   || {};
    const fc        = res.forecast      || {};
    const contract  = res.data_contract || {};
    const chartType = (contract.chart_type || '').toLowerCase();
    const qs        = res.query_summary || {};

    const histPoints: { label: string; value: number }[] =
      (desc.data_points || []).map((p: any) => ({ label: String(p.label), value: Number(p.value) }));
    const fcPoints: { label: string; value: number }[] =
      (fc.data_points || []).map((p: any) => ({ label: String(p.label), value: Number(p.value) }));

    const newWidgets: AutoWidget[] = [];

    // ── KPI cards ────────────────────────────────────────────────────────────
    const descKpis: { label: string; value: any; color: string }[] = [];
    if (desc.value != null) {
      descKpis.push({ label: 'Historical', value: desc.value, color: '#2563eb' });
    }
    if (fc.value != null) {
      descKpis.push({
        label: fc.forecast_window ? 'Forecast (' + fc.forecast_window + ')' : 'Forecast',
        value: fc.value,
        color: '#f59e0b'
      });
    }
    if (descKpis.length > 0) {
      newWidgets.push({ type: 'kpi-group', title: qs.original_query || 'Result', col: 12, kpis: descKpis });
    }

    // Determine column width: side-by-side when both charts are visible
    const bothVisible = this.activeView === 'both' && histPoints.length > 0 && fcPoints.length > 0;
    const chartCol = bothVisible ? 6 : 12;

    // ── Historical chart ──────────────────────────────────────────────────────
    if (histPoints.length > 0) {
      newWidgets.push({
        type: 'chart',
        subtype: 'historical',
        title: 'Historical Trend',
        col: chartCol,
        chartOptions: this.buildHistoricalChart(histPoints)
      });
    }

    // ── Forecast chart ────────────────────────────────────────────────────────
    if (fcPoints.length > 0) {
      newWidgets.push({
        type: 'chart',
        subtype: 'forecast',
        title: 'Forecast — ' + (fc.forecast_window || 'Upcoming'),
        col: chartCol,
        chartOptions: this.buildForecastChart(fcPoints)
      });
    }

    // ── Breakdown chart (non-KPI only) ────────────────────────────────────────
    if (desc.breakdown && desc.breakdown.length > 0 && chartType !== 'kpi') {
      const subWidgets = this.analyzeArray(desc.breakdown, qs.metric || 'Breakdown');
      subWidgets.forEach(w => { w.subtype = 'breakdown'; newWidgets.push(w); });
    }

    this.setWidgets(newWidgets);
  }

  private buildHistoricalChart(points: { label: string; value: number }[]): Highcharts.Options {
    return {
      chart: { type: 'line', animation: false },
      title: { text: '' },
      xAxis: { categories: points.map(p => p.label), crosshair: true },
      yAxis: { title: { text: '' }, min: 0 },
      tooltip: { shared: true },
      series: [{
        type: 'line',
        name: 'Historical',
        data: points.map(p => p.value),
        color: '#2563eb',
        dashStyle: 'Solid',
        marker: { enabled: true, radius: 4 }
      }],
      legend: { enabled: false },
      credits: { enabled: false }
    };
  }

  private buildForecastChart(points: { label: string; value: number }[]): Highcharts.Options {
    return {
      chart: { type: 'line', animation: false },
      title: { text: '' },
      xAxis: { categories: points.map(p => p.label), crosshair: true },
      yAxis: { title: { text: '' }, min: 0 },
      tooltip: { shared: true },
      series: [{
        type: 'line',
        name: 'Forecast',
        data: points.map(p => p.value),
        color: '#f59e0b',
        dashStyle: 'Dash',
        marker: { enabled: true, radius: 4 }
      }],
      legend: { enabled: false },
      credits: { enabled: false }
    };
  }

  private renderFromResponse(res: any): void {
    const widgetResults: any[] = res.widgetResults || [];
    if (widgetResults.length === 0) return;

    const renderData: any[] = [];
    widgetResults.forEach(w => {
      if (w.error || !w.rows || w.rows.length === 0) return;
      const rows = w.rows as Record<string, any>[];
      const type: string = (w.widgetType || '').toUpperCase();
      if (type === 'KPI') {
        const kpiObj: Record<string, any> = {};
        rows.forEach(r => { kpiObj[w.title || 'value'] = r['value'] ?? r['VALUE'] ?? Object.values(r)[0]; });
        renderData.push(kpiObj);
      } else {
        renderData.push(rows);
      }
    });

    if (renderData.length > 0) {
      const detected = this.analyzeData(renderData.length === 1 ? renderData[0] : renderData);
      this.setWidgets(detected);
    }
  }

  private analyzeData(data: any): AutoWidget[] {
    if (data === null || data === undefined) {
      return [{ type: 'message', title: '', col: 12, message: 'Data is null or undefined.' }];
    }
    if (Array.isArray(data)) {
      if (data.length === 0) {
        return [{ type: 'message', title: '', col: 12, message: 'Empty array — no data to display.' }];
      }
      return this.analyzeArray(data);
    }
    if (typeof data === 'object') return this.analyzeObject(data, 0);
    return [{ type: 'message', title: 'Value', col: 12, message: String(data) }];
  }

  private analyzeObject(obj: Record<string, any>, depth: number): AutoWidget[] {
    const widgets: AutoWidget[] = [];
    const entries = Object.entries(obj);
    const numericEntries = entries.filter(([, v]) => typeof v === 'number');
    const arrayEntries   = entries.filter(([, v]) => Array.isArray(v));

    if (numericEntries.length > 0) {
      widgets.push({
        type: 'kpi-group', title: depth === 0 ? 'Summary' : '', col: 12,
        kpis: numericEntries.map(([k, v], i) => ({
          label: this.formatLabel(k), value: v, color: KPI_COLORS[i % KPI_COLORS.length]
        }))
      });
    }
    for (const [key, arr] of arrayEntries) {
      widgets.push(...this.analyzeArray(arr, key));
    }
    return widgets;
  }

  private analyzeArray(arr: any[], contextKey?: string): AutoWidget[] {
    const widgets: AutoWidget[] = [];
    const titleBase = contextKey ? this.formatLabel(contextKey) : '';

    if (typeof arr[0] !== 'object' || arr[0] === null) {
      widgets.push({
        type: 'chart', title: titleBase || 'Values', col: 12,
        chartOptions: this.makeBarChart(arr.map((_, i) => String(i + 1)), [{ name: 'Value', data: arr.map(v => Number(v)) }])
      });
      return widgets;
    }

    const allKeys     = Object.keys(arr[0]);
    const numericKeys = allKeys.filter(k => arr.every(r => typeof r[k] === 'number' || (!isNaN(Number(r[k])) && String(r[k]).trim() !== '' && r[k] !== null)));
    const stringKeys  = allKeys.filter(k => !numericKeys.includes(k));
    const dateKeys    = stringKeys.filter(k => arr.some(r => DATE_PATTERN.test(String(r[k]))));
    const catKeys     = stringKeys.filter(k => !dateKeys.includes(k));

    const labelKey     = dateKeys[0] || catKeys[0];
    const isTimeSeries = dateKeys.length > 0;
    const chartTitle   = titleBase || (isTimeSeries ? 'Trend' : 'Distribution');

    if (labelKey && numericKeys.length >= 1) {
      const labels = arr.map(r => String(r[labelKey]));
      if (isTimeSeries) {
        widgets.push({ type: 'chart', title: chartTitle, col: 12,
          chartOptions: this.makeLineChart(labels, numericKeys.map(k => ({ name: this.formatLabel(k), data: arr.map(r => Number(r[k])) }))) });
      } else if (numericKeys.length === 1 && arr.length <= 7) {
        widgets.push({ type: 'chart', title: chartTitle, col: 6,
          chartOptions: this.makePieChart(this.formatLabel(numericKeys[0]), arr.map(r => ({ name: String(r[labelKey]), y: Number(r[numericKeys[0]]) }))) });
      } else if (numericKeys.length === 1) {
        widgets.push({ type: 'chart', title: chartTitle, col: 12,
          chartOptions: this.makeBarChart(labels, [{ name: this.formatLabel(numericKeys[0]), data: arr.map(r => Number(r[numericKeys[0]])) }]) });
      } else {
        widgets.push({ type: 'chart', title: chartTitle, col: 12,
          chartOptions: this.makeColumnChart(labels, numericKeys.map(k => ({ name: this.formatLabel(k), data: arr.map(r => Number(r[k])) }))) });
      }
    } else if (numericKeys.length >= 1) {
      widgets.push({ type: 'chart', title: chartTitle || 'Values', col: 12,
        chartOptions: this.makeColumnChart(arr.map((_, i) => String(i + 1)), numericKeys.map(k => ({ name: this.formatLabel(k), data: arr.map(r => Number(r[k])) }))) });
    }

    const tableCol   = widgets.length > 0 && widgets[widgets.length - 1].col === 6 ? 6 : 12;
    const tableTitle = titleBase ? titleBase + ' — Records' : 'Records';
    widgets.push({
      type: 'table', title: tableTitle, col: tableCol,
      columns: allKeys.map(k => this.formatLabel(k)),
      rows: arr.map(r => allKeys.map(k => (r[k] !== undefined && r[k] !== null) ? String(r[k]) : ''))
    });
    return widgets;
  }

  private makeBarChart(categories: string[], series: { name: string; data: number[] }[]): Highcharts.Options {
    return { chart: { type: 'bar', animation: false }, title: { text: '' },
      xAxis: { categories }, yAxis: { title: { text: '' } },
      series: series.map(s => ({ ...s, type: 'bar' as const })),
      legend: { enabled: series.length > 1 }, credits: { enabled: false } };
  }

  private makeColumnChart(categories: string[], series: { name: string; data: number[] }[]): Highcharts.Options {
    return { chart: { type: 'column', animation: false }, title: { text: '' },
      xAxis: { categories }, yAxis: { title: { text: '' } },
      series: series.map(s => ({ ...s, type: 'column' as const })),
      legend: { enabled: series.length > 1 }, credits: { enabled: false } };
  }

  private makeLineChart(categories: string[], series: { name: string; data: number[] }[]): Highcharts.Options {
    return { chart: { type: 'line', animation: false }, title: { text: '' },
      xAxis: { categories }, yAxis: { title: { text: '' } },
      series: series.map(s => ({ ...s, type: 'line' as const })),
      legend: { enabled: series.length > 1 }, credits: { enabled: false } };
  }

  private makePieChart(seriesName: string, data: { name: string; y: number }[]): Highcharts.Options {
    return { chart: { type: 'pie', animation: false }, title: { text: '' },
      tooltip: { pointFormat: '<b>{point.percentage:.1f}%</b> ({point.y})' },
      plotOptions: { pie: { allowPointSelect: true, cursor: 'pointer',
        dataLabels: { enabled: true, format: '<b>{point.name}</b>: {point.y}' } } },
      series: [{ type: 'pie', name: seriesName, data }], credits: { enabled: false } };
  }

  formatLabel(key: string): string {
    return key.replace(/_/g, ' ').replace(/([a-z])([A-Z])/g, '$1 $2')
      .split(' ').map(w => w.charAt(0).toUpperCase() + w.slice(1).toLowerCase()).join(' ');
  }

  trendIcon(direction: string): string {
    return direction === 'UP' ? '↑' : direction === 'DOWN' ? '↓' : direction === 'STABLE' ? '→' : '?';
  }

  trendColor(direction: string): string {
    return direction === 'UP' ? '#10b981' : direction === 'DOWN' ? '#ef4444' : '#6b7280';
  }

  onChartInstance(chart: Highcharts.Chart, widgetId: string): void {
    this.chartRefs.set(widgetId, chart);
  }

  exportChartPng(widget: AutoWidget): void {
    const chart = this.chartRefs.get(widget.id!);
    if (!chart) return;
    const svg = chart.getSVG({ chart: { width: 900, height: 420 } } as any);
    const blob = new Blob([svg], { type: 'image/svg+xml;charset=utf-8' });
    const url = URL.createObjectURL(blob);
    const img = new Image();
    img.onload = () => {
      const canvas = document.createElement('canvas');
      canvas.width = 900; canvas.height = 420;
      const ctx = canvas.getContext('2d')!;
      ctx.fillStyle = '#ffffff';
      ctx.fillRect(0, 0, canvas.width, canvas.height);
      ctx.drawImage(img, 0, 0);
      URL.revokeObjectURL(url);
      canvas.toBlob(pngBlob => {
        if (!pngBlob) return;
        const a = document.createElement('a');
        a.href = URL.createObjectURL(pngBlob);
        a.download = (widget.title || 'chart') + '.png';
        a.click();
      });
    };
    img.src = url;
  }

  exportCSV(widget: AutoWidget): void {
    if (!widget.columns || !widget.rows) return;
    const escape = (v: string) => '"' + v.replace(/"/g, '""') + '"';
    const csv = [widget.columns.map(escape).join(','),
      ...widget.rows.map(r => r.map(escape).join(','))].join('\n');
    const a = document.createElement('a');
    a.href = URL.createObjectURL(new Blob([csv], { type: 'text/csv' }));
    a.download = (widget.title || 'export') + '.csv';
    a.click();
  }

  private setWidgets(list: AutoWidget[]): void {
    list.forEach((w, i) => { w.id = 'w_' + (++this.widgetSeq) + '_' + i; });
    this.chartRefs.clear();
    this.widgets = [];
    setTimeout(() => { this.widgets = list; }, 0);
  }
}
